import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type { Node, Edge } from 'reactflow';
import { flowEditorState, type FlowHistoryItem } from './flowEditorState';
import { restoreNodeContent } from './useUndoRedo';
import { pruneEmptyMessageBubbles } from './nodes/pruneEmptyMessageBubbles';
import { isEmptyMessageBubble } from './nodes/flowEditorBlockNodeTypes';
import type { JSONContent } from '@tiptap/react';
import { isFlowContentEqual } from './contentEquality';
import { classifyFlowChange } from './undoInspector/classifyFlowChange';
import { undoInspectorState } from './undoInspector/undoInspectorState';
import { decideRecord } from './recordDecision';

// -- Undo recorder ---------------------------------------------------------------
// One undo entry per genuine edit, coalescing a typing run. recordHistory() reads
// the committed graph (not the clientVersion bump) from a post-commit effect, or a
// structural edit reads the pre-edit arrays.

// A typing pause past this ends a run. Above the 300ms node-data sync so a run's
// debounced commit lands first.
const TEXT_PAUSE_MS = 700;

// A detached copy so a recorded entry never aliases live node.data that a later
// writer reassigns.
function cloneGraph(graph: FlowHistoryItem): FlowHistoryItem {
  return {
    nodes: graph.nodes.map((node) => ({ ...node, data: { ...node.data } })),
    edges: graph.edges.map((edge) => ({ ...edge })),
  };
}

// A copy with empty placeholder bubbles dropped. Apply before any content compare or
// a transient empty bubble reads as a change.
function pruneEmptyBubblesFromGraph(graph: FlowHistoryItem): FlowHistoryItem {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const pruned = pruneEmptyMessageBubbles(node.data?.tiptap);
      return pruned ? { ...node, data: { ...node.data, tiptap: pruned } } : node;
    }),
  };
}

// Overlay the active node's live editor content onto a snapshot, putting the recorder
// on the keystroke clock rather than the node-data debounce. Pure.
export function mergeActiveEditorContent(
  snapshot: FlowHistoryItem,
  active: NonNullable<ReturnType<typeof flowEditorState.getActiveEditorContent>>,
): FlowHistoryItem {
  return {
    ...snapshot,
    nodes: snapshot.nodes.map((node) =>
      node.id === active.id ? { ...node, data: { ...node.data, tiptap: active.tiptap } } : node,
    ),
  };
}

// Whether `next` is `before` plus exactly one trailing EMPTY bubble on one node: the
// shape Enter commits. The placeholder belongs to the NEXT message, so this is a
// boundary, not its own entry. Any other change returns false.
function isEmptyTrailingBubbleAdd(before: FlowHistoryItem, next: FlowHistoryItem): boolean {
  if (before.nodes.length !== next.nodes.length || before.edges.length !== next.edges.length) {
    return false;
  }
  const beforeById = new Map(before.nodes.map((node) => [node.id, node]));
  let sawBoundary = false;
  for (const after of next.nodes) {
    const prior = beforeById.get(after.id);
    if (!prior) {
      return false;
    }
    const priorBlocks: JSONContent[] = prior.data?.tiptap?.content ?? [];
    const afterBlocks: JSONContent[] = after.data?.tiptap?.content ?? [];
    if (afterBlocks.length === priorBlocks.length) {
      continue;
    }
    // Only a single node gaining exactly one trailing empty bubble qualifies.
    if (sawBoundary || afterBlocks.length !== priorBlocks.length + 1) {
      return false;
    }
    const added = afterBlocks[afterBlocks.length - 1];
    if (!added || !isEmptyMessageBubble(added)) {
      return false;
    }
    // The placeholder must be purely additive: everything but the new trailing
    // block must equal the node's previous content.
    const nodeMinusPlaceholder: Node = {
      ...after,
      data: { ...after.data, tiptap: { ...after.data.tiptap, content: afterBlocks.slice(0, -1) } },
    };
    if (
      !isFlowContentEqual(
        { nodes: [prior], edges: [] },
        { nodes: [nodeMinusPlaceholder], edges: [] },
      )
    ) {
      return false;
    }
    sawBoundary = true;
  }
  return sawBoundary;
}

type UseUndoRecorder = (args: {
  nodes: Node[];
  edges: Edge[];
  // Pushes the pre-edit graph onto the undo past stack and returns a handle that
  // undoes exactly that push (used to retract a typing run that reverts to its start).
  commitSnapshot: (item: FlowHistoryItem) => () => void;
}) => {
  // Call from a post-commit effect on [nodes, edges]; records one undo entry per
  // genuine change, coalescing a typing run into one.
  recordHistory: () => void;
  // Seed the baseline on init so the first edit's "before" is the loaded graph.
  seedBaseline: (graph: FlowHistoryItem) => void;
};

export const useUndoRecorder: UseUndoRecorder = ({ nodes, edges, commitSnapshot }) => {
  // The graph as the undo history last recorded it: the "before" the next genuine
  // edit pushes onto the past stack.
  const lastRecordedGraphRef = useRef<FlowHistoryItem | null>(null);

  // Render-synced mirror of the controlled nodes/edges. Read from HERE, not
  // flowEditor.getNodes(), whose queryable store lags by a render.
  const liveGraphRef = useRef<FlowHistoryItem>({ nodes, edges });
  liveGraphRef.current = { nodes, edges };

  // The node whose typing run is open, or null. The run ends on a different node, a
  // pause past TEXT_PAUSE_MS, a restore, or any non-text edit.
  const textSessionNodeRef = useRef<string | null>(null);

  // Whether the open run has pushed its entry: the first text commit records, later
  // ones only advance the baseline.
  const textRunRecordedRef = useRef(false);

  // The graph when the open run began (the "before" we pushed) and the handle that
  // undoes that push, so a run reverting to its start retracts and leaves no trace.
  const textRunStartGraphRef = useRef<FlowHistoryItem | null>(null);
  const retractTextRunRef = useRef<(() => void) | null>(null);

  // Restore-driven clientVersion bumps to ignore (undo/redo markDirty() to autosave,
  // but must not record). A consume-on-read counter survives the recorder's turn delay.
  const restoreSuppressRef = useRef(0);

  // Suppress the active-editor overlay for the first capture after a restore: the
  // remounted editor still holds PRE-restore text, which would splice back over the
  // restored baseline. Consume-on-read.
  const overlaySuppressRef = useRef(false);

  // The clientVersion last processed. Only a genuine edit bumps it, so an unchanged
  // version means selection/measurement/drag churn and we skip the deep diff.
  const lastSeenClientVersionRef = useRef(0);

  // A copy of the committed graph. Must NOT flush the editor, or the flush would
  // write -> commit -> re-run the recorder, looping.
  const captureFlowSnapshot = useCallback((): FlowHistoryItem => {
    return cloneGraph(liveGraphRef.current);
  }, []);

  // Overlay the selected node's live keystrokes onto a base graph, UNLESS suppressed
  // for the first capture after a restore. Consume the suppression.
  const overlayActiveEditor = useCallback((base: FlowHistoryItem): FlowHistoryItem => {
    if (overlaySuppressRef.current) {
      overlaySuppressRef.current = false;
      return base;
    }
    const active = flowEditorState.getActiveEditorContent();
    return active ? mergeActiveEditorContent(base, active) : base;
  }, []);

  // The committed graph with the selected node's un-committed keystrokes overlaid,
  // so redo restores the exact text typed since the last sync.
  const captureCurrentGraph = useCallback(
    (): FlowHistoryItem => overlayActiveEditor(captureFlowSnapshot()),
    [captureFlowSnapshot, overlayActiveEditor],
  );

  // Clear the open run's bookkeeping without touching the stacks. Use when a restore
  // ends the run (the undo flow owns the stacks): let go of the retract handle, don't fire it.
  const resetTextRun = useCallback(() => {
    textSessionNodeRef.current = null;
    textRunRecordedRef.current = false;
    textRunStartGraphRef.current = null;
    retractTextRunRef.current = null;
  }, []);

  // End the open run, retracting its entry if it reverted to exactly where it
  // started so a net-zero run leaves no trace. NOT for restores (use resetTextRun):
  // a restore already rewrote the stacks, so a stale retract handle corrupts them.
  const endTextRun = useCallback(() => {
    if (textRunRecordedRef.current && retractTextRunRef.current && textRunStartGraphRef.current) {
      const start = textRunStartGraphRef.current;
      // Prune empty bubbles before comparing, or a transient empty bubble reads as a
      // change and the net-zero run wouldn't retract.
      if (
        isFlowContentEqual(
          pruneEmptyBubblesFromGraph(start),
          pruneEmptyBubblesFromGraph(captureCurrentGraph()),
        )
      ) {
        retractTextRunRef.current();
        lastRecordedGraphRef.current = start;
        undoInspectorState.logEvent('retracted-noop-run', null);
      }
    }
    resetTextRun();
  }, [captureCurrentGraph, resetTextRun]);

  // End the session after a typing pause. Re-armed on every keystroke by noteTextEdit.
  const endTextSessionOnPause = useDebouncedCallback(() => {
    endTextRun();
  }, TEXT_PAUSE_MS);

  // End the session NOW so the next keystroke starts a fresh run. Called by undo/redo
  // and a discrete edit, which must always break a run.
  const closeTextSession = useCallback(() => {
    endTextSessionOnPause.cancel();
    endTextRun();
  }, [endTextSessionOnPause, endTextRun]);

  // Finalise the just-completed message as its own undo entry and open a fresh run for
  // the next, WITHOUT ending the session. Called synchronously on appending Enter's
  // empty placeholder: an unambiguous boundary, not one inferred from a diff that
  // races the debounce (fast typing would coalesce two messages into one undo step).
  const finaliseTextRun = useCallback(() => {
    if (textSessionNodeRef.current === null) {
      return;
    }
    const current = captureCurrentGraph();
    const start = textRunStartGraphRef.current ?? lastRecordedGraphRef.current;
    if (!textRunRecordedRef.current && start && !isFlowContentEqual(start, current)) {
      // The completed message never recorded a keystroke entry (Enter on a fresh
      // bubble, or typing faster than the debounce). Push its coupled entry now so
      // the message is one undoable unit. Log it, or the push has no inspector line.
      commitSnapshot(start);
      undoInspectorState.logEvent(
        'recorded',
        classifyFlowChange(start, current) ?? { kind: 'edit', label: 'edit', summary: '' },
      );
    }
    // Re-baseline to current (post-flush: completed text plus the new empty bubble),
    // reset the run, keep the session open for the next message's own run.
    lastRecordedGraphRef.current = current;
    textRunStartGraphRef.current = current;
    textRunRecordedRef.current = false;
    retractTextRunRef.current = null;
    // Pin the pre-gate to this boundary's version so the deferred recordHistory sees
    // no new version and skips. Without this, both this push and the deferred pass
    // fire, double-recording the message.
    lastSeenClientVersionRef.current = flowEditorState.clientVersion;
    endTextSessionOnPause();
  }, [commitSnapshot, captureCurrentGraph, endTextSessionOnPause]);

  // A keystroke landed in `nodeId`. If a different node's run was open, end it first
  // so typing never coalesces across nodes, then this node owns the session.
  const noteTextEdit = useCallback(
    (nodeId: string) => {
      if (textSessionNodeRef.current !== nodeId && textSessionNodeRef.current !== null) {
        endTextSessionOnPause.cancel();
        endTextRun();
      }
      textSessionNodeRef.current = nodeId;
      endTextSessionOnPause();
    },
    [endTextSessionOnPause, endTextRun],
  );

  const recordHistory = useCallback(() => {
    // Pre-gate: an unchanged clientVersion means selection/measurement/drag churn,
    // not a genuine edit. Skip the deep diff.
    if (flowEditorState.clientVersion === lastSeenClientVersionRef.current) {
      return;
    }
    lastSeenClientVersionRef.current = flowEditorState.clientVersion;

    if (restoreSuppressRef.current > 0) {
      // Bump from an undo/redo restore, already applied by applyRestoredGraph.
      // Consume one suppression and ignore it, or the restore records a phantom
      // entry and clears the redo branch.
      restoreSuppressRef.current -= 1;
      undoInspectorState.logEvent('suppressed-restore', null, flowEditorState.lastDirtySource);
      return;
    }
    const before = lastRecordedGraphRef.current;
    const next = captureCurrentGraph();

    // Gate out bumps that didn't change the conversation (blur, selection-only,
    // reverted keystroke) so redo survives. Prune empty bubbles first, or the
    // deselect prune of a trailing placeholder records a phantom "delete message".
    if (
      !before ||
      isFlowContentEqual(pruneEmptyBubblesFromGraph(before), pruneEmptyBubblesFromGraph(next))
    ) {
      undoInspectorState.logEvent('gated-noop', null, flowEditorState.lastDirtySource);
      return;
    }

    // 'edit' fallback so a real change always carries a label.
    const action = classifyFlowChange(before, next) ?? {
      kind: 'edit' as const,
      label: 'edit',
      summary: '',
    };

    // Slow-typing counterpart to finaliseTextRun: the debounce already committed, so
    // Enter's empty placeholder reaches the recorder as a diff. Finalise the
    // completed message and open a fresh run for the next.
    if (textSessionNodeRef.current !== null && isEmptyTrailingBubbleAdd(before, next)) {
      if (!textRunRecordedRef.current) {
        // Message never recorded a keystroke entry; push its coupled entry now.
        commitSnapshot(textRunStartGraphRef.current ?? before);
      }
      lastRecordedGraphRef.current = next;
      textRunStartGraphRef.current = next;
      textRunRecordedRef.current = false;
      retractTextRunRef.current = null;
      endTextSessionOnPause();
      undoInspectorState.logEvent('recorded', action, flowEditorState.lastDirtySource);
      return;
    }

    // Typing into an empty node promotes its starter paragraph into the first bubble,
    // reading as a 'message-add' though it starts a typing run. Treat it as text
    // while a session is open, or it records discretely and orphans the first message
    // from the boundary logic.
    const actionKind =
      action.kind === 'message-add' && textSessionNodeRef.current !== null
        ? ('message-edit' as const)
        : action.kind;

    // Coalesce a typing run into one entry; record each discrete edit on its own.
    const decision = decideRecord({
      actionKind,
      textSessionOpen: textSessionNodeRef.current !== null,
      runAlreadyRecorded: textRunRecordedRef.current,
    });

    if (decision === 'coalesce') {
      lastRecordedGraphRef.current = next;
      undoInspectorState.logEvent('text-run-coalesced', null);
      return;
    }

    const retract = commitSnapshot(before);
    lastRecordedGraphRef.current = next;
    if (decision === 'open-text-run') {
      textRunRecordedRef.current = true;
      // Remember the run's start and this push's handle so endTextRun can retract it
      // if the run reverts to `before`.
      textRunStartGraphRef.current = before;
      retractTextRunRef.current = retract;
    } else {
      closeTextSession();
    }
    undoInspectorState.logEvent('recorded', action, flowEditorState.lastDirtySource);
  }, [commitSnapshot, captureCurrentGraph, closeTextSession, endTextSessionOnPause]);

  // Register the recorder's interface on the singleton so useUndoRedo (in a different
  // component) can drive undo/redo without prop-drilling.
  useEffect(() => {
    flowEditorState.getCurrentGraph = captureCurrentGraph;

    // What undo/redo stash: the baseline ref (synchronously current, so a burst of
    // presses stashes the right intermediate graph) with live keystrokes overlaid.
    flowEditorState.getRecordedGraph = () =>
      overlayActiveEditor(cloneGraph(lastRecordedGraphRef.current ?? liveGraphRef.current));

    flowEditorState.noteTextEdit = noteTextEdit;

    // Flush first so the completed message's text is committed before finaliseTextRun
    // re-baselines.
    flowEditorState.endTextRunNow = () => {
      flowEditorState.flushActiveEditor();
      finaliseTextRun();
    };

    // Apply a restored graph and re-baseline the recorder in one synchronous turn.
    flowEditorState.applyRestoredGraph = (rawGraph) => {
      // Prune empty placeholder bubbles FIRST. The restore remounts the selected
      // node, whose unmount prune+flush would otherwise be a second, unsuppressed
      // markDirty the recorder logs as a phantom "delete message". Pre-pruned, the
      // remount finds nothing to remove.
      const graph = pruneEmptyBubblesFromGraph(rawGraph);
      flowEditorState.flowEditor?.setNodes(() => graph.nodes.map(restoreNodeContent));
      flowEditorState.flowEditor?.setEdges(() => graph.edges);
      lastRecordedGraphRef.current = cloneGraph(graph);
      // Drop the open run WITHOUT firing its retract handle: the undo flow already
      // rewrote the stacks, so the handle is stale and would corrupt them.
      endTextSessionOnPause.cancel();
      resetTextRun();
      restoreSuppressRef.current += 1;
      // Suppress the overlay for the next capture: the remounted editor still holds
      // pre-restore text, which would splice back over the restored baseline.
      overlaySuppressRef.current = true;
      flowEditorState.markRestored();
      flowEditorState.markDirty('restore');
    };

    return () => {
      flowEditorState.getCurrentGraph = () => ({ nodes: [], edges: [] });
      flowEditorState.getRecordedGraph = () => ({ nodes: [], edges: [] });
      flowEditorState.applyRestoredGraph = () => {};
      flowEditorState.noteTextEdit = () => {};
      flowEditorState.endTextRunNow = () => {};
    };
  }, [
    captureCurrentGraph,
    overlayActiveEditor,
    noteTextEdit,
    finaliseTextRun,
    endTextSessionOnPause,
    resetTextRun,
  ]);

  // Seed the baseline from the loaded graph (from onInit) so the first edit's
  // "before" is the loaded state, not an empty graph.
  const seedBaseline = useCallback((graph: FlowHistoryItem) => {
    lastRecordedGraphRef.current = cloneGraph(graph);
  }, []);

  return { recordHistory, seedBaseline };
};
